import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from '../auth/AuthContext';
import { fetchStores } from '../api';
import { generateCams } from '../data/cameraData';

const DashboardContext = createContext(null);

const STORE_COLORS = ['#d97706','#0d9488','#2563eb','#7c3aed','#dc2626','#0891b2','#16a34a','#b45309','#be185d','#1d4ed8','#0f766e','#9333ea'];

const DATE_STORAGE_KEY = 'a2b-dashboard:dateRange';

function loadStoredDateRange() {
  try {
    const raw = sessionStorage.getItem(DATE_STORAGE_KEY);
    if (!raw) return null;
    const { start, end } = JSON.parse(raw);
    if (!start || !end) return null;
    return { start: new Date(start), end: new Date(end) };
  } catch { return null; }
}

function fmtDate(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDatesInRange(start, end) {
  if (!start || !end) return [fmtDate(new Date())];
  const dates = [];
  const d = new Date(start.getTime());
  const e = new Date(end.getTime());
  // Safety: max 90 days
  let guard = 0;
  while (d <= e && guard < 90) {
    dates.push(fmtDate(d));
    d.setDate(d.getDate() + 1);
    guard++;
  }
  return dates.length ? dates : [fmtDate(new Date())];
}

export function DashboardProvider({ children }) {
  const today = new Date();
  const { clientStores, storesLoading } = useAuth();
  const [stores, setStores]                 = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selectedStores, setSelectedStores] = useState(new Set());
  const [activeStore, setActiveStore]       = useState(null);
  const [dateRange, setDateRange]           = useState(() => loadStoredDateRange() || { start: today, end: today });

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      try {
        sessionStorage.setItem(DATE_STORAGE_KEY, JSON.stringify({
          start: dateRange.start.toISOString(),
          end: dateRange.end.toISOString(),
        }));
      } catch { /* ignore quota/parse errors */ }
    }
  }, [dateRange]);
  const [camsByStore, setCamsByStore]        = useState({});
  const [baseImageMap, setBaseImageMap]      = useState({});
  const [baseImageLoaded, setBaseImageLoaded] = useState(false);

  // Fetch baseImage URLs from our /api/stores (the v3 headerStores_v2 API
  // doesn't carry them). Keyed by storeId, merged into the store list below.
  useEffect(() => {
    fetchStores()
      .then(data => {
        const map = {};
        (data?.stores || []).forEach(s => {
          if (s.storeId && s.baseImage) map[s.storeId] = s.baseImage;
        });
        setBaseImageMap(map);
      })
      .catch(err => console.warn('Could not load store baseImages:', err?.message || err))
      .finally(() => setBaseImageLoaded(true));
  }, []);

  // Stores come from the real /v3 headerStores_v2 API (loaded in AuthContext as
  // clientStores). We map that list into the shape the dashboard screens expect,
  // pulling extra identity fields from the raw API store when present.
  // Gate on baseImageLoaded so the first render already has real baseImage and
  // store cards don't flash the CCTV placeholder.
  useEffect(() => {
    if (storesLoading || !baseImageLoaded) { setLoading(true); return; }

    const raw = Array.isArray(clientStores) ? clientStores : [];
    const storeList = raw.map((s, i) => {
      const r = s._raw || {};
      const profile = r.storeProfile || {};
      return {
        storeId:  s.id,
        name:     s.name,
        color:    STORE_COLORS[i % STORE_COLORS.length],
        active:   (r.status ?? 'active') === 'active',
        location: s.area || profile.address || '',
        open:     profile.open || r.open || '07:00:00',
        close:    profile.close || r.close || '23:00:00',
        timeZone: profile.timeZone || r.timeZone || 'Asia/Kolkata',
        baseImage: baseImageMap[s.id] || r.baseImage || '',
      };
    });

    setStores(storeList);
    setSelectedStores(new Set(storeList.map(s => s.name)));

    const cams = {};
    storeList.forEach(s => { cams[s.name] = generateCams(s.name); });
    setCamsByStore(cams);
    setLoading(false);
  }, [clientStores, storesLoading, baseImageMap, baseImageLoaded]);

  const currentStore = useMemo(
    () => activeStore ? stores.find(s => s.name === activeStore) : null,
    [activeStore, stores]
  );

  const visibleStores = useMemo(
    () => stores.filter(s => selectedStores.has(s.name)),
    [selectedStores, stores]
  );

  // Date helpers for API calls
  const startDate = dateRange.start ? fmtDate(dateRange.start) : fmtDate(today);
  const endDate = dateRange.end ? fmtDate(dateRange.end) : startDate;
  const dateList = getDatesInRange(dateRange.start || today, dateRange.end || today);
  const datesParam = dateList.join(',');

  const kpis = useMemo(() => {
    const list = activeStore ? stores.filter(s => s.name === activeStore) : visibleStores;
    return {
      storeIds: list.map(s => s.storeId).join(','),
    };
  }, [activeStore, visibleStores, stores]);

  function enterStore(name) {
    setActiveStore(name);
    setSelectedStores(new Set([name]));
  }

  function exitStore() {
    setActiveStore(null);
    setSelectedStores(new Set(stores.map(s => s.name)));
  }

  function toggleStore(name) {
    setSelectedStores(prev => {
      const next = new Set(prev);
      if (next.has(name)) { if (next.size > 1) next.delete(name); }
      else next.add(name);
      // Auto-switch view based on selection
      if (next.size === 1) {
        setActiveStore([...next][0]);
      } else {
        setActiveStore(null);
      }
      return next;
    });
  }

  function selectAllStores() {
    setSelectedStores(new Set(stores.map(s => s.name)));
    setActiveStore(null);
  }

  function clearAllStores() {
    if (stores.length) {
      setSelectedStores(new Set([stores[0].name]));
      setActiveStore(stores[0].name);
    }
  }

  return (
    <DashboardContext.Provider value={{
      stores, loading,
      selectedStores, setSelectedStores,
      activeStore, setActiveStore,
      currentStore,
      visibleStores,
      kpis,
      dateRange, setDateRange,
      startDate, endDate, datesParam,
      camsByStore,
      enterStore, exitStore,
      toggleStore, selectAllStores, clearAllStores,
    }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used inside DashboardProvider');
  return ctx;
}
